import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import Header1 from "../components/default-header";
import DefaultFooter from "../components/default-footer";
import { API_URL, Back_url } from "../config";

/* ------------------------------------------------------------------ */
/*  Types — mirrors CarController::show() / BookingCarController::show() */
/* ------------------------------------------------------------------ */

interface CarPhoto {
  id: number;
  path: string;
  is_main: boolean;
}

interface CarSummary {
  id: number;
  name: string;
  model: string;
  number: string;
  color: string;
  engine_capacity: string;
  type: string;
  price_per_day: string;
  price_per_month: string;
  price_per_year: string;
  photos: CarPhoto[];
}

interface BookingDetail {
  id: number;
  client_info: {
    email: string;
    firstname: string;
    lastname: string;
    phone: string;
    address: string;
  };
  booking_info: {
    booking_date: string;
    end_date: string | null;
    duration: number;
    rate_type: "day" | "month" | "year";
    new_price: string;
    document_type: string | null;
    insurance_document: string | null;
    notes: string | null;
    status: "pending" | "confirmed" | "cancelled" | "completed";
    created_at: string;
  };
  car: CarSummary | null;
}

/* ------------------------------------------------------------------ */
/*  Icons                                                              */
/* ------------------------------------------------------------------ */

const Icon = {
  Check: () => (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  ),
  Pin: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s7-6.4 7-12a7 7 0 1 0-14 0c0 5.6 7 12 7 12z" /><circle cx="12" cy="10" r="2.5" />
    </svg>
  ),
  Mail: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m2 7 10 6 10-6" />
    </svg>
  ),
  Phone: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.68 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.32 1.85.55 2.81.68A2 2 0 0 1 22 16.92z" />
    </svg>
  ),
  Print: () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="6 9 6 2 18 2 18 9" /><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  ),
  Shield: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  ),
};

const CarPlaceholder = () => (
  <svg width="80" height="80" viewBox="0 0 200 200" fill="none" aria-hidden="true">
    <path
      d="M45 115 L60 85 C65 75 75 70 90 70 L125 70 C140 70 150 78 158 88 L168 105 C173 107 175 111 175 116 V125 C175 128 172 130 168 130 H162 C162 122 154 115 145 115 C136 115 128 122 128 130 H72 C72 122 64 115 55 115 C46 115 38 122 38 130 H32 C28 130 25 128 25 125 V118 C25 115 28 112 32 112 Z"
      fill="var(--teal-d)" fillOpacity="0.06" stroke="var(--gold)" strokeWidth="1.5"
    />
    <circle cx="55" cy="130" r="9" stroke="var(--gold)" strokeWidth="2" fill="var(--cream)" />
    <circle cx="145" cy="130" r="9" stroke="var(--gold)" strokeWidth="2" fill="var(--cream)" />
  </svg>
);

const STATUS_META: Record<BookingDetail["booking_info"]["status"], { label: string; color: string; bg: string }> = {
  pending: { label: "Pending confirmation", color: "#a3893f", bg: "#f1e9c8" },
  confirmed: { label: "Confirmed", color: "#2f7d5e", bg: "#e4f1ea" },
  cancelled: { label: "Cancelled", color: "#b0483c", bg: "#f7e4e0" },
  completed: { label: "Completed", color: "#3c6474", bg: "#e8eef0" },
};

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "long", year: "numeric" }) : "—";

const BookingConfirmation = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation() as { state?: { booking?: any; car?: any } };

  const [booking, setBooking] = useState<BookingDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    // If we arrived straight from the booking form, we already have everything
    // we need in navigation state — show it instantly, then reconcile with the
    // server so a page refresh (or a shared link) still works correctly.
    const stateBooking = location.state?.booking;
    if (stateBooking) {
      setBooking(normalizeFromCreate(stateBooking, location.state?.car));
      setLoading(false);
    }

    const fetchBooking = async () => {
      try {
        if (!stateBooking) setLoading(true);
        setError(false);
        const res = await axios.get(`${API_URL}/booking-cars/${id}`);
        setBooking(res.data.data ?? res.data);
      } catch (err) {
        console.error("Error fetching booking:", err);
        if (!stateBooking) setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchBooking();
    window.scrollTo(0, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // The POST /booking-cars response shape (flat) differs from GET /booking-cars/{id}
  // (nested). Normalize the flat shape so the page can render immediately.
  function normalizeFromCreate(flat: any, car: any): BookingDetail {
    return {
      id: flat.id,
      client_info: {
        email: flat.email,
        firstname: flat.firstname,
        lastname: flat.lastname,
        phone: flat.phone,
        address: flat.address,
      },
      booking_info: {
        booking_date: flat.booking_date,
        end_date: flat.end_date,
        duration: Number(flat.duration),
        rate_type: flat.rate_type,
        new_price: flat.new_price,
        document_type: flat.document_type,
        insurance_document: flat.insurance_document,
        notes: flat.notes,
        status: flat.status ?? "pending",
        created_at: flat.created_at,
      },
      car: car ?? flat.car ?? null,
    };
  }

  const photo = booking?.car?.photos?.find((p) => p.is_main) || booking?.car?.photos?.[0];
  const status = booking ? STATUS_META[booking.booking_info.status] : STATUS_META.pending;
  const ref = booking ? `#${String(booking.id).padStart(6, "0")}` : "";

  return (
    <>
      <style>{`
        :root {
          --teal: #294b5a; --teal-d: #1a3140; --teal-l: #3b6272;
          --gold: #d3be76; --gold-l: #ede4b5; --gold-d: #b09a50;
          --cream: #f7f4ee; --mist: #edeae2; --silver: #8a9aa3; --ink: #1a2f38;
          --line: rgba(41,75,90,.13);
        }
        .bc-page { font-family: 'DM Sans', sans-serif; background: var(--cream); min-height: 100vh; color: var(--ink); }
        .bc-page h1, .bc-page h2, .bc-page h3 { font-family: 'Playfair Display', serif; }
        .bc-mono { font-family: 'DM Mono', monospace; }
        .bc-wrap { max-width: 760px; margin: 0 auto; padding: 130px 24px 90px; }

        .bc-hero { text-align: center; margin-bottom: 34px; }
        .bc-check {
          width: 68px; height: 68px; border-radius: 50%; margin: 0 auto 18px;
          background: var(--gold-l); color: var(--gold-d);
          display: flex; align-items: center; justify-content: center;
          animation: bc-pop .45s cubic-bezier(.2,.9,.3,1.3);
        }
        @keyframes bc-pop { from { transform: scale(.5); opacity: 0; } to { transform: scale(1); opacity: 1; } }
        .bc-hero h1 { font-size: 26px; margin: 0 0 8px; color: var(--ink); }
        .bc-hero p { font-size: 14px; color: var(--silver); margin: 0; }
        .bc-ref { display: inline-block; margin-top: 10px; font-size: 12px; letter-spacing: .5px; color: var(--teal-l); background: #fff; border: 1px solid var(--line); border-radius: 30px; padding: 5px 14px; }

        .bc-card {
          background: #fff; border-radius: 16px; border: 1px solid var(--line);
          box-shadow: 0 16px 40px rgba(26,49,64,.08); overflow: hidden;
        }

        .bc-status-bar { display: flex; align-items: center; justify-content: space-between; padding: 14px 26px; }
        .bc-status-pill { font-size: 11.5px; font-weight: 700; letter-spacing: .3px; padding: 6px 14px; border-radius: 30px; }

        .bc-vehicle { display: flex; gap: 16px; align-items: center; padding: 20px 26px; border-top: 1px solid var(--line); border-bottom: 1px solid var(--line); }
        .bc-vehicle-photo { width: 96px; height: 72px; border-radius: 10px; overflow: hidden; background: var(--mist); flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
        .bc-vehicle-photo img { width: 100%; height: 100%; object-fit: cover; }
        .bc-vehicle-name { font-size: 17px; font-weight: 600; margin: 0; }
        .bc-vehicle-sub { font-size: 12.5px; color: var(--silver); margin: 3px 0 0; }

        .bc-timeline { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; padding: 22px 26px; }
        @media (max-width: 560px) { .bc-timeline { grid-template-columns: 1fr; } }
        .bc-tl-item { position: relative; padding-left: 20px; }
        .bc-tl-item::before { content: ''; position: absolute; left: 0; top: 4px; width: 10px; height: 10px; border-radius: 50%; background: var(--gold-d); }
        .bc-tl-item.drop::before { background: var(--teal); }
        .bc-tl-label { font-size: 10.5px; text-transform: uppercase; letter-spacing: .5px; color: var(--silver); font-weight: 700; }
        .bc-tl-date { font-size: 14px; font-weight: 600; color: var(--ink); margin-top: 3px; }

        .bc-section { padding: 4px 26px 22px; }
        .bc-section-title { font-size: 11px; text-transform: uppercase; letter-spacing: .5px; color: var(--silver); font-weight: 700; margin: 18px 0 10px; }
        .bc-row { display: flex; justify-content: space-between; gap: 12px; font-size: 13px; color: var(--teal-l); padding: 5px 0; border-bottom: 1px dashed var(--line); }
        .bc-row:last-child { border-bottom: none; }
        .bc-row b { color: var(--ink); font-weight: 600; text-align: right; }

        .bc-total-band { background: var(--cream); margin: 6px 26px 24px; border-radius: 10px; padding: 14px 16px; display: flex; justify-content: space-between; align-items: baseline; }
        .bc-total-band span { font-size: 12.5px; color: var(--teal-l); }
        .bc-total-band b { font-size: 20px; color: var(--gold-d); font-family: 'DM Mono', monospace; }

        .bc-next { background: var(--teal-d); color: #fff; padding: 22px 26px; }
        .bc-next h4 { margin: 0 0 8px; font-size: 14px; font-family: 'DM Sans', sans-serif; font-weight: 700; color: var(--gold-l); }
        .bc-next p { margin: 0; font-size: 12.5px; line-height: 1.7; color: #d8e1e5; }
        .bc-next-contacts { display: flex; gap: 18px; margin-top: 12px; flex-wrap: wrap; }
        .bc-next-contacts span { display: flex; align-items: center; gap: 6px; font-size: 12.5px; color: #d8e1e5; }

        .bc-actions { display: flex; gap: 10px; padding: 22px 0 0; }
        .bc-btn {
          flex: 1; text-align: center; text-decoration: none; cursor: pointer;
          border-radius: 9px; padding: 12px; font-size: 13.5px; font-weight: 600; transition: all .2s;
        }
        .bc-btn.primary { background: var(--teal); color: #fff; border: none; }
        .bc-btn.primary:hover { background: var(--teal-d); }
        .bc-btn.secondary { background: #fff; color: var(--teal); border: 1px solid var(--line); display: flex; align-items: center; justify-content: center; gap: 7px; }
        .bc-btn.secondary:hover { border-color: var(--teal); }

        .bc-trust { display: flex; align-items: center; justify-content: center; gap: 6px; font-size: 11.5px; color: var(--silver); margin-top: 20px; }

        .bc-state { text-align: center; padding: 140px 24px; color: var(--silver); }

        @media print {
          header, footer, .bc-actions, .bc-trust { display: none !important; }
          .bc-wrap { padding: 20px; }
          .bc-page { background: #fff; }
        }
      `}</style>

      <div className="bc-page">
        <Header1 />

        {loading && <div className="bc-state"><p>Loading your booking...</p></div>}
        {!loading && (error || !booking) && (
          <div className="bc-state">
            <p>We couldn't find this booking.</p>
            <Link to="/car-rental" style={{ color: "var(--teal)" }}>← Back to fleet</Link>
          </div>
        )}

        {!loading && booking && (
          <div className="bc-wrap">
            <div className="bc-hero">
              <div className="bc-check"><Icon.Check /></div>
              <h1>Your booking request is in</h1>
              <p>We've sent a confirmation email to {booking.client_info.email}.</p>
              <div className="bc-ref">Booking reference <strong>{ref}</strong></div>
            </div>

            <div className="bc-card">
              <div className="bc-status-bar">
                <span style={{ fontSize: 12.5, color: "var(--silver)" }}>
                  Requested {fmtDate(booking.booking_info.created_at)}
                </span>
                <span className="bc-status-pill" style={{ color: status.color, background: status.bg }}>
                  {status.label}
                </span>
              </div>

              <div className="bc-vehicle">
                <div className="bc-vehicle-photo">
                  {photo ? <img src={`${Back_url}/${photo.path}`} alt={booking.car?.name} /> : <CarPlaceholder />}
                </div>
                <div>
                  <p className="bc-vehicle-name">{booking.car?.name ?? "Vehicle"}</p>
                  <p className="bc-vehicle-sub">
                    {booking.car?.model} · {booking.car?.number} seats · {booking.car?.type}
                  </p>
                </div>
              </div>

              <div className="bc-timeline">
                <div className="bc-tl-item">
                  <div className="bc-tl-label">Pick-up</div>
                  <div className="bc-tl-date">{fmtDate(booking.booking_info.booking_date)}</div>
                </div>
                <div className="bc-tl-item drop">
                  <div className="bc-tl-label">Drop-off</div>
                  <div className="bc-tl-date">
                    {booking.booking_info.end_date
                      ? fmtDate(booking.booking_info.end_date)
                      : `${booking.booking_info.duration} ${booking.booking_info.rate_type}(s) later`}
                  </div>
                </div>
              </div>

              <div className="bc-section">
                <div className="bc-section-title">Driver</div>
                <div className="bc-row"><span>Name</span><b>{booking.client_info.firstname} {booking.client_info.lastname}</b></div>
                <div className="bc-row"><span>Phone</span><b>{booking.client_info.phone}</b></div>
                <div className="bc-row"><span>Address</span><b>{booking.client_info.address}</b></div>
                {booking.booking_info.document_type && (
                  <div className="bc-row">
                    <span>Document</span>
                    <b style={{ textTransform: "capitalize" }}>{booking.booking_info.document_type.replace("_", " ")}</b>
                  </div>
                )}
                {booking.booking_info.notes && (
                  <div className="bc-row"><span>Notes</span><b>{booking.booking_info.notes}</b></div>
                )}
              </div>

              <div className="bc-total-band">
                <span>Total price · {booking.booking_info.duration} {booking.booking_info.rate_type}(s)</span>
                <b className="bc-mono">{Number(booking.booking_info.new_price).toFixed(2)} OMR</b>
              </div>

              <div className="bc-next">
                <h4>What happens next</h4>
                <p>
                  Our team reviews every request against real-time availability and will reach out to confirm
                  your booking. Please keep your phone nearby — no payment is needed until pick-up.
                </p>
                <div className="bc-next-contacts">
                  <span><Icon.Mail /> info@silverjarz.com</span>
                  <span><Icon.Phone /> +968 9357 9571</span>
                </div>
              </div>
            </div>

            <div className="bc-actions">
              <Link to="/car-rent" className="bc-btn primary">Browse more vehicles</Link>
              <button className="bc-btn secondary" onClick={() => window.print()}>
                <Icon.Print /> Save / Print
              </button>
            </div>

            <div className="bc-trust"><Icon.Shield /> This page is safe to bookmark and revisit anytime</div>
          </div>
        )}

        <DefaultFooter />
      </div>
    </>
  );
};

export default BookingConfirmation;